/**
 * Onglet « Clients B2B » — page unifiée Équipe & Magasins.
 * Liste les entreprises inscrites via le portail B2B (lecture seule).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Loader2, ShieldCheck } from 'lucide-react';

import Layout from '../components/Layout';
import AdminEquipeMagasinsTabs from '../components/AdminEquipeMagasinsTabs';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

// Les clés de statut (valide / en_attente / refuse) proviennent du backend : NE PAS traduire.
// Seul le libellé affiché est i18n — repli sur la valeur brute pour un statut inconnu.
const B2B_STATUS = {
  valide: { labelKey: 'adminB2B.statusValide', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  en_attente: { labelKey: 'adminB2B.statusEnAttente', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  refuse: { labelKey: 'adminB2B.statusRefuse', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
};

export default function AdminB2BCompaniesTab() {
  const navigate = useNavigate();
  const { api, isAdmin } = useAuth();
  const { t, formatDate } = useLanguage();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get('/admin/b2b/companies');
        setCompanies(r.data || []);
      } catch {
        setCompanies([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [api, isAdmin]);

  if (!isAdmin) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto p-6 text-center">
          <ShieldCheck className="w-12 h-12 mx-auto text-red-400 mb-3" />
          <h1 className="text-xl font-bold text-white">{t('adminB2B.accessDenied')}</h1>
          <p className="text-gray-400 mt-2">{t('adminB2B.reservedAdmins')}</p>
          <button onClick={() => navigate('/dashboard')} className="mt-4 text-emerald-400 hover:text-emerald-300">
            ← {t('adminB2B.backToDashboard')}
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <AdminEquipeMagasinsTabs active="b2b" />
        <header className="flex items-center justify-between mb-4">
          <div>
            <button onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white text-sm inline-flex items-center gap-1 mb-1">
              <ArrowLeft className="w-3.5 h-3.5" /> {t('adminB2B.back')}
            </button>
            <h1 className="text-2xl font-bold text-white inline-flex items-center gap-2">
              <Building2 className="w-6 h-6 text-cyan-400" /> {t('adminB2B.title')}
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              {companies.length !== 1
                ? t('adminB2B.accountsPlural', { count: companies.length })
                : t('adminB2B.accountsSingular', { count: companies.length })}
            </p>
          </div>
        </header>

        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden" data-testid="b2b-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-dark-custom">
              <thead className="bg-gray-800/60 text-gray-400 text-xs uppercase">
                <tr>
                  <th className="text-left p-3">{t('adminB2B.colCompany')}</th>
                  <th className="text-left p-3">{t('adminB2B.colManager')}</th>
                  <th className="text-left p-3">{t('adminB2B.colEmail')}</th>
                  <th className="text-left p-3">{t('adminB2B.colPhone')}</th>
                  <th className="text-left p-3">SIRET</th>
                  <th className="text-left p-3">{t('adminB2B.colStatus')}</th>
                  <th className="text-left p-3">{t('adminB2B.colRegistered')}</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} className="p-6 text-center text-gray-500">
                      <Loader2 className="w-5 h-5 animate-spin inline" />
                    </td>
                  </tr>
                )}
                {!loading && companies.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-6 text-center text-gray-400">
                      {t('adminB2B.empty')}
                    </td>
                  </tr>
                )}
                {!loading && companies.map((c) => {
                  const st = B2B_STATUS[c.status] || { labelKey: null, color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' };
                  return (
                    <tr key={c.id} className="border-t border-gray-800" data-testid={`b2b-row-${c.id}`}>
                      <td className="p-3 font-semibold text-white">{c.company_name}</td>
                      <td className="p-3 text-gray-300">{c.manager_name}</td>
                      <td className="p-3 text-gray-400">{c.email}</td>
                      <td className="p-3 text-gray-400">{c.phone}</td>
                      <td className="p-3 text-gray-400 font-mono text-xs">{c.siret}</td>
                      <td className="p-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold border ${st.color}`}>
                          {st.labelKey ? t(st.labelKey) : c.status}
                        </span>
                      </td>
                      <td className="p-3 text-gray-400">
                        {c.created_at ? formatDate(c.created_at) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
